import type { Rng } from '../../core/rng';
import type { Vec2 } from '../../geo/types';
import { settingOf } from './houseSetting';
import { lotFrame } from './lotFrame';
import { openingsFor, type FacadeSpec, type HouseOpenings, type Opening } from './openings';
import type { Lot, WaterWalls } from './types';

/**
 * PLAN Lots, back buildings on the water (2026-10-06). A back building opens only onto its own
 * courtyard, because its other walls stand against the plots around it. On a bank they do not: the
 * Nes plots' rear buildings stand with a gable on the Rokin, and from a boat their blank walls read
 * as unfinished blocks. A wall with water within `reachM` of it, at `least` of the places looked
 * from, is therefore an open wall and gets openings as a house's back or side does, and a gable wall
 * a door at the water in `doorRate` of them, to land goods from a boat.
 *
 * That these walls had windows and water doors follows the painting of 1538 and the woodcut of 1544,
 * which draw the Rokin's east side as house backs standing in the water. Which building had which
 * opening is not recorded: the counts, sizes and the door's rate are estimates.
 */
export const WATER_WALL = {
  reachM: [1, 2, 3],
  stations: [0.25, 0.5, 0.75],
  least: 2,
  doorRate: 0.5,
  door: { width: 1.2, height: 2.0, sill: 0.18, headroom: 0.5, clearance: 0.3 },
} as const;

type Wall = keyof WaterWalls;

/* Each wall of a lot from one end to the other, with the way out of the building through it. */
function wallsOf(lot: Lot): Record<Wall, { from: Vec2; to: Vec2; out: Vec2 }> {
  const { frontMid, facing, right, width, depth, shear = 0 } = lotFrame(lot);
  const corner = (side: number, back: number): Vec2 => [
    frontMid[0] + right[0] * (side * (width / 2) + back * shear * depth) - facing[0] * back * depth,
    frontMid[1] + right[1] * (side * (width / 2) + back * shear * depth) - facing[1] * back * depth,
  ];
  return {
    back: { from: corner(-1, 1), to: corner(1, 1), out: [-facing[0], -facing[1]] },
    left: { from: corner(-1, 0), to: corner(-1, 1), out: [-right[0], -right[1]] },
    right: { from: corner(1, 0), to: corner(1, 1), out: right },
  };
}

/** Which of a lot's back and side walls stand on the water, or null when none does. */
export function wallsOnWater(lot: Lot, isWater: (p: Vec2) => boolean): WaterWalls | null {
  const walls = wallsOf(lot);
  const wet = ({ from, to, out }: { from: Vec2; to: Vec2; out: Vec2 }): boolean =>
    WATER_WALL.stations.filter((t) => {
      const at: Vec2 = [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t];
      return WATER_WALL.reachM.some((d) => isWater([at[0] + out[0] * d, at[1] + out[1] * d]));
    }).length >= WATER_WALL.least;
  const found = { back: wet(walls.back), left: wet(walls.left), right: wet(walls.right) };
  return found.back || found.left || found.right ? found : null;
}

/**
 * The back buildings with their walls on the water marked. The Lastage's sheds and yard buildings
 * stay as the painting draws them: plain timber sheds on the shore.
 */
export function markWaterWalls(
  backhouses: readonly Lot[],
  isWater: (p: Vec2) => boolean
): readonly Lot[] {
  return backhouses.map((lot) => {
    if (settingOf(lot.sourceId) === 'lastage') return lot;
    const onWater = wallsOnWater(lot, isWater);
    return onWater ? { ...lot, onWater } : lot;
  });
}

/* A door at the water in the middle of a gable wall, in place of the window that stood there. */
function withWaterDoor(spec: FacadeSpec, back: readonly Opening[]): readonly Opening[] {
  const { width, height, sill, headroom, clearance } = WATER_WALL.door;
  const door: Opening = {
    kind: 'loftDoor',
    x: (spec.width - width) / 2,
    y: sill,
    width,
    height: Math.min(height, spec.storeyHeight - headroom),
  };
  const beside = (o: Opening): boolean =>
    o.y >= spec.storeyHeight ||
    o.x + o.width <= door.x - clearance ||
    o.x >= door.x + door.width + clearance;
  return [door, ...back.filter(beside)];
}

/**
 * A back building's openings with its walls on the water opened: the courtyard front as it was, and
 * each wet wall as a house's back or side, a wet gable with its attic light.
 */
export function waterWallOpenings(
  spec: FacadeSpec,
  courtyard: HouseOpenings,
  walls: WaterWalls,
  rng: Rng
): HouseOpenings {
  const open = openingsFor(spec, rng.fork('layout'));
  const landing = walls.back && rng.fork('door').chance(WATER_WALL.doorRate);
  return {
    ...courtyard,
    back: walls.back ? (landing ? withWaterDoor(spec, open.back) : open.back) : courtyard.back,
    backGable: walls.back ? open.backGable : courtyard.backGable,
    left: walls.left ? open.left.filter((o) => o.kind !== 'shop') : courtyard.left,
    right: walls.right ? open.right.filter((o) => o.kind !== 'shop') : courtyard.right,
  };
}
